import React, { useCallback, useState } from 'react';
import ContentAddIcon from 'material-ui/svg-icons/content/add-circle-outline';
import ContentRemoveIcon from 'material-ui/svg-icons/content/remove-circle-outline';
import RegisterClass from './RegisterClass';
import { IconButton } from 'material-ui';

/** @param {object[]} classes */
function registerClassesHaveDuplicates(classes) {
    const seenNames = new Set();
    const seenIds = new Set();
    return classes.some((c) => {
        const name = (c && c.name != null ? String(c.name) : '').trim().toLowerCase();
        const id = (c && c._id != null ? String(c._id) : '').trim();
        if (name) {
            if (seenNames.has(name)) return true;
            seenNames.add(name);
        }
        if (id) {
            if (seenIds.has(id)) return true;
            seenIds.add(id);
        }
        return false;
    });
}

/**
 * @param {object[]} rows
 * @param {number} rowIndex
 * @param {object[]} changes
 */
function applyRegisterClassChanges(rows, rowIndex, changes) {
    const next = rows.map((row, i) => (i === rowIndex ? { ...row } : row));
    let target = { ...next[rowIndex] };

    changes.forEach(({ field, value }) => {
        if (field === 'registerClass') {
            target = { ...value };
        } else if (typeof field === 'string' && field.startsWith('registerClass.')) {
            const key = field.replace('registerClass.', '');
            target = { ...target, [key]: value };
        }
    });

    next[rowIndex] = target;
    return next;
}

function isRegisterClassPatch(field) {
    return field === 'registerClass' || (typeof field === 'string' && field.startsWith('registerClass.'));
}

const DUPLICATE_MSG = 'This register class is already selected (same name or class).';

/**
 * @param {{
 *   confirmed: boolean,
 *   registerClasses: object[],
 *   changeField: Function,
 *   getError: Function,
 *   clearError: Function,
 *   modifiedFields?: string[],
 *   underStyle?: import('react').CSSProperties,
 * }} props
 */
function RegisterClasses({
    confirmed, registerClasses, modifiedFields, underStyle, changeField,
    getError, clearError,
}) {
    const [duplicateErrors, setDuplicateErrors] = useState({});

    const mapChangesForRow = useCallback((index, changes) => changes.map((ch) => {
        if (!isRegisterClassPatch(ch.field)) {
            return ch;
        }
        return {
            ...ch,
            field: ch.field === 'registerClass'
                ? `registerClasses.${index}`
                : `registerClasses.${index}.${ch.field.replace('registerClass.', '')}`,
            value: ch.value,
        };
    }), []);

    const handleAdd = () => {
        setDuplicateErrors({});
        changeField({
            field: 'registerClasses',
            value: [...registerClasses, { _id: '', name: '' }],
        });
    };

    const handleRemove = (index) => {
        setDuplicateErrors({});
        const value = registerClasses.slice();
        value.splice(index, 1);
        changeField({
            field: 'registerClasses',
            value,
        });
    };

    const handleChange = (index, rawChanges) => {
        const changes = Array.isArray(rawChanges) ? rawChanges : [rawChanges];
        const registerPatches = changes.filter(ch => isRegisterClassPatch(ch.field));

        if (registerPatches.length) {
            const pending = applyRegisterClassChanges(registerClasses, index, registerPatches);
            if (registerClassesHaveDuplicates(pending)) {
                setDuplicateErrors(prev => ({ ...prev, [index]: DUPLICATE_MSG }));
                return;
            }
        }

        setDuplicateErrors((prev) => {
            if (!(index in prev)) return prev;
            const next = { ...prev };
            delete next[index];
            return next;
        });

        changeField(mapChangesForRow(index, changes));
    };

    return (
        <React.Fragment>
            {registerClasses.map((registerClass, index) => {
                let rowGetError;
                if (duplicateErrors[index]) {
                    rowGetError = () => duplicateErrors[index];
                } else if (index === 0) {
                    rowGetError = () => getError('registerClasses');
                } else {
                    rowGetError = () => '';
                }
                return (
                    <div className='row' key={`${registerClass._id}-${index}`}>
                        <div className='col-xs-6'>
                            <RegisterClass
                                registerClass={registerClass}
                                changeField={change => handleChange(index, change)}
                                confirmed={!confirmed}
                                getError={rowGetError}
                                underStyle={modifiedFields && modifiedFields.includes(`registerClasses.${index}.name`) ? underStyle : null}
                                clearError={clearError}
                            />
                        </div>
                        <div className='col-xs-2'>
                            {index === 0 && (
                                <IconButton
                                    onClick={handleAdd}
                                    style={{ margin: '28px 0 0 8px' }}
                                >
                                    <ContentAddIcon color='green' />
                                </IconButton>
                            )}
                            {index !== 0 && (
                                <IconButton
                                    onClick={() => handleRemove(index)}
                                    style={{ margin: '28px 0 0 8px' }}
                                >
                                    <ContentRemoveIcon color='red' />
                                </IconButton>
                            )}
                        </div>
                    </div>
                );
            })}
        </React.Fragment>
    );
}

export default RegisterClasses;



// WEBPACK FOOTER //
// ./app/components/Vessels/Single/Rows/RegisterClasses.jsx